/**
 * Xero Contacts Global Cache with IndexedDB
 *
 * Actual contacts are stored in IndexedDB:
 *   Application → IndexedDB → XeroContactsCacheDB → contacts → xero_contacts_cache
 *
 * localStorage is only used for mutation/sync notification:
 *   xero_contacts_mutation
 */

import { getXeroRequest, postXeroRequest } from './xeroRequest'

const CACHE_KEY = 'xero_contacts_cache'
const XERO_CONTACTS_MUTATION_KEY = 'xero_contacts_mutation'

const DB_NAME = 'XeroContactsCacheDB'
const STORE_NAME = 'contacts'

// IMPORTANT:
// Version 2 forces IndexedDB upgrade and creates the missing "contacts" store.
const DB_VERSION = 2

const CACHE_TTL_MS = 2 * 60 * 60 * 1000 // 2 hours

let dbInstance = null
let pendingContactsRequest = null

/* ============================= Helpers ============================= */

export const getXeroContactsArray = (xeroData) => {
  const contacts =
    xeroData?.body?.body?.Contacts ||
    xeroData?.body?.Contacts ||
    xeroData?.Contacts ||
    xeroData?.data?.body?.body?.Contacts ||
    xeroData?.data?.body?.Contacts ||
    []

  return Array.isArray(contacts) ? contacts : []
}

const getXeroContactCount = (xeroData) => getXeroContactsArray(xeroData).length

function deleteXeroContactsDB() {
  return new Promise((resolve, reject) => {
    try {
      if (dbInstance) {
        dbInstance.close()
        dbInstance = null
      }

      const request = indexedDB.deleteDatabase(DB_NAME)

      request.onsuccess = () => {
        console.log('[Xero Contacts] Deleted broken IndexedDB database')
        resolve(true)
      }

      request.onerror = () => {
        console.error('[Xero Contacts] Failed to delete broken IndexedDB database')
        reject(request.error)
      }

      request.onblocked = () => {
        console.warn('[Xero Contacts] Delete database blocked. Close other tabs using this app.')
        resolve(false)
      }
    } catch (error) {
      reject(error)
    }
  })
}

/* ============================= IndexedDB ============================= */

async function initXeroContactsDB(repairAttempted = false) {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      if (dbInstance.objectStoreNames.contains(STORE_NAME)) {
        resolve(dbInstance)
        return
      }

      dbInstance.close()
      dbInstance = null
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => {
      console.error('[Xero Contacts] IndexedDB failed to open:', request.error)
      reject(request.error)
    }

    request.onsuccess = async () => {
      const db = request.result

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        console.warn(
          `[Xero Contacts] Object store "${STORE_NAME}" is missing. Repairing database...`,
        )

        db.close()
        dbInstance = null

        if (repairAttempted) {
          reject(new Error(`Missing IndexedDB object store: ${STORE_NAME}`))
          return
        }

        try {
          await deleteXeroContactsDB()
          const repairedDb = await initXeroContactsDB(true)
          resolve(repairedDb)
        } catch (error) {
          reject(error)
        }

        return
      }

      dbInstance = db
      console.log('[Xero Contacts] ✅ IndexedDB initialized')
      resolve(dbInstance)
    }

    request.onupgradeneeded = (event) => {
      const db = event.target.result

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' })
        console.log('[Xero Contacts] ✅ IndexedDB contacts store created')
      }
    }
  })
}

async function getFromIndexedDB() {
  try {
    if (!dbInstance) await initXeroContactsDB()

    return new Promise((resolve) => {
      const transaction = dbInstance.transaction(STORE_NAME, 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.get(CACHE_KEY)

      request.onsuccess = () => {
        const cached = request.result

        if (!cached) {
          console.log('[Xero Contacts] IndexedDB MISS')
          resolve(null)
          return
        }

        const cacheAge = Date.now() - cached.timestamp
        const isValid = cacheAge < CACHE_TTL_MS

        if (isValid) {
          const ageMinutes = Math.round(cacheAge / 60000)
          const contactCount = getXeroContactCount(cached.data)

          console.log(
            `[Xero Contacts] IndexedDB HIT (${contactCount} contacts, ${ageMinutes}min old)`,
          )

          resolve(cached.data)
          return
        }

        console.log('[Xero Contacts] IndexedDB data EXPIRED')
        resolve(null)
      }

      request.onerror = () => {
        console.error('[Xero Contacts] IndexedDB read failed:', request.error)
        resolve(null)
      }
    })
  } catch (error) {
    console.error('[Xero Contacts] IndexedDB read failed:', error.message)
    return null
  }
}

async function saveToIndexedDB(data) {
  try {
    if (!dbInstance) await initXeroContactsDB()

    return new Promise((resolve) => {
      const transaction = dbInstance.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)

      const request = store.put({
        key: CACHE_KEY,
        data,
        timestamp: Date.now(),
        expiresAt: new Date(Date.now() + CACHE_TTL_MS).toISOString(),
      })

      request.onsuccess = () => {
        const contactCount = getXeroContactCount(data)
        console.log(`[Xero Contacts] ✅ Saved ${contactCount} contacts to IndexedDB`)
        resolve(true)
      }

      request.onerror = () => {
        console.error('[Xero Contacts] IndexedDB write failed:', request.error)
        resolve(false)
      }
    })
  } catch (error) {
    console.error('[Xero Contacts] IndexedDB write failed:', error.message)
    return false
  }
}

async function removeFromIndexedDB() {
  try {
    if (!dbInstance) await initXeroContactsDB()

    return new Promise((resolve) => {
      const transaction = dbInstance.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.delete(CACHE_KEY)

      request.onsuccess = () => {
        console.log('[Xero Contacts] Cache removed from IndexedDB')
        resolve(true)
      }

      request.onerror = () => {
        console.error('[Xero Contacts] Failed to remove cache:', request.error)
        resolve(false)
      }
    })
  } catch (error) {
    console.error('[Xero Contacts] Failed to remove cache:', error.message)
    return false
  }
}

/* ============================= Mutation Broadcast ============================= */

function broadcastXeroContactsMutation() {
  try {
    const payload = {
      type: 'refresh',
      timestamp: Date.now(),
    }

    // Same-browser / same-user tab sync
    localStorage.setItem(XERO_CONTACTS_MUTATION_KEY, JSON.stringify(payload))

    // Cross-user / cross-computer sync
    postXeroRequest('/api/xero-contacts-mutation', {
      type: 'refresh',
      timestamp: new Date().toISOString(),
    }).catch((err) => {
      console.debug('[Xero Contacts] Failed to broadcast mutation:', err.message)
    })
  } catch (error) {
    console.debug('[Xero Contacts] Failed to broadcast:', error.message)
  }
}

/* ============================= Public API ============================= */

/**
 * Get Xero contacts from IndexedDB.
 * If cache is missing or expired, fetch fresh from API.
 */
export const getCachedXeroContacts = async () => {
  if (pendingContactsRequest) return pendingContactsRequest

  pendingContactsRequest = (async () => {
    try {
      const cached = await getFromIndexedDB()

      if (cached) {
        const contactCount = getXeroContactCount(cached)
        console.log(`[Xero Contacts] Cache HIT (${contactCount} contacts)`)
        return cached
      }

      console.log('[Xero Contacts] Fetching fresh from API...')

      const response = await getXeroRequest('/api/get_xero_contacts')
      const responseData = response.data || { body: { body: { Contacts: [] } } }

      const contacts = getXeroContactsArray(responseData)

      if (!contacts.length) {
        console.warn('[Xero Contacts] API returned no contacts')
      }

      await saveToIndexedDB(responseData)

      console.log(`[Xero Contacts] Fetched ${contacts.length} contacts`)
      return responseData
    } catch (error) {
      console.error('[Xero Contacts] Failed to get contacts:', error.message)
      return { body: { body: { Contacts: [] } } }
    } finally {
      pendingContactsRequest = null
    }
  })()

  return pendingContactsRequest
}

/**
 * Read only from cache.
 * Does NOT fetch from API.
 */
export const getXeroContactsFromCache = async () => {
  try {
    const cached = await getFromIndexedDB()

    if (!cached) {
      console.log('[Xero Contacts] Cache is empty or expired')
      return null
    }

    const contactCount = getXeroContactCount(cached)
    console.log(`[Xero Contacts] Cache read (${contactCount} contacts)`)

    return cached
  } catch (error) {
    console.error('[Xero Contacts] Failed to read from cache:', error.message)
    return null
  }
}

/**
 * Clear cache.
 *
 * broadcast=true:
 *   Use when the current user manually refreshes contacts.
 *
 * broadcast=false:
 *   Use when another tab/user already sent the mutation.
 *   This prevents localStorage mutation loops.
 */
export const clearXeroContactsCache = async ({ broadcast = true } = {}) => {
  try {
    await removeFromIndexedDB()

    if (broadcast) {
      console.log('[Xero Contacts] Cache cleared, broadcasting refresh')
      broadcastXeroContactsMutation()
    } else {
      console.log('[Xero Contacts] Cache cleared silently')
    }

    return true
  } catch (error) {
    console.error('[Xero Contacts] Failed to clear cache:', error.message)
    return false
  }
}

/**
 * Force refresh cache from API.
 */
export const refreshXeroContactsCache = async ({ broadcast = true } = {}) => {
  try {
    console.log('[Xero Contacts] Force refreshing cache...')

    await clearXeroContactsCache({ broadcast: false })

    const response = await getXeroRequest('/api/get_xero_contacts')
    const responseData = response.data || { body: { body: { Contacts: [] } } }

    await saveToIndexedDB(responseData)

    const contactCount = getXeroContactCount(responseData)
    console.log(`[Xero Contacts] ✅ Force refreshed ${contactCount} contacts`)

    if (broadcast) {
      broadcastXeroContactsMutation()
    }

    return responseData
  } catch (error) {
    console.error('[Xero Contacts] Failed to refresh contacts:', error.message)
    return { body: { body: { Contacts: [] } } }
  }
}

/**
 * Update cache with supplied Xero data.
 *
 * broadcast=false:
 *   Use when saving contacts that came from normal page load/order API.
 *
 * broadcast=true:
 *   Use when contacts were actually changed/refreshed by the user.
 */
export const updateXeroContactsCache = async (xeroData, { broadcast = false } = {}) => {
  try {
    if (!xeroData) {
      console.warn('[Xero Contacts] Cannot update cache with empty data')
      return null
    }

    console.log('[Xero Contacts] Updating cache with new data...')

    await saveToIndexedDB(xeroData)

    const contactCount = getXeroContactCount(xeroData)
    console.log(`[Xero Contacts] ✅ Updated cache with ${contactCount} contacts`)

    if (broadcast) {
      broadcastXeroContactsMutation()
    }

    return xeroData
  } catch (error) {
    console.error('[Xero Contacts] Failed to update cache:', error.message)
    return null
  }
}
